import React, { useMemo } from 'react';
import Chat from '@/pages/Chat';
import { MapleWord } from './MapleWord';
import EditorTabBar, { EditorTabItem } from './EditorTabBar';
import AIProjectDetail from './AIProjectDetail';

export type AIProjectWorkspaceTab = {
  id: string;
  kind: 'overview' | 'chat' | 'document';
  title: string;
  path?: string;
  chatId?: string;
  filePath?: string;
  initialContent?: string;
};

export type AIProjectWorkspaceState = {
  tabs: AIProjectWorkspaceTab[];
  activeId: string;
};

interface AIProjectWorkspaceProps {
  projectId: string;
  state: AIProjectWorkspaceState;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onCloseOthers: (id: string) => void;
  onCloseRight: (id: string) => void;
  onNewChat: () => void;
  onOpenChat: (chatId: string, title?: string) => void;
  onOpenDocument: (title: string, filePath: string, initialContent?: string) => void;
}

const AIProjectWorkspace = ({
  projectId,
  state,
  onSelect,
  onClose,
  onReorder,
  onCloseOthers,
  onCloseRight,
  onNewChat,
  onOpenChat,
  onOpenDocument,
}: AIProjectWorkspaceProps) => {
  const editorTabs = useMemo<EditorTabItem[]>(() => {
    return state.tabs.map((tab) => ({
      id: tab.id,
      name: tab.title,
      path: tab.path || '',
      isPinned: tab.kind === 'overview',
    }));
  }, [state.tabs]);

  const activeTab = useMemo(() => {
    return state.tabs.find((tab) => tab.id === state.activeId) || state.tabs[0];
  }, [state.activeId, state.tabs]);

  if (!activeTab) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="px-4 pt-4">
        <div className="rounded-2xl bg-muted/40 p-1.5 shadow-sm ring-1 ring-border/60">
          <EditorTabBar
            tabs={editorTabs}
            activeId={state.activeId}
            onSelect={onSelect}
            onClose={onClose}
            onReorder={onReorder}
            onNewUntitled={onNewChat}
            onCloseOthers={onCloseOthers}
            onCloseRight={onCloseRight}
            onSave={() => undefined}
            onSaveAll={() => undefined}
            onRevealInExplorer={() => undefined}
            onCopyPath={() => undefined}
            showSaveItems={false}
            showRevealItems={false}
            variant="pill"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 pt-4">
        {activeTab.kind === 'overview' ? (
          <AIProjectDetail
            key={`${projectId}:overview`}
            projectId={projectId}
            onOpenChat={onOpenChat}
            onOpenDocument={onOpenDocument}
          />
        ) : activeTab.kind === 'chat' ? (
          <div className="h-full">
            <Chat
              key={activeTab.id}
              chatIdOverride={activeTab.chatId}
              embeddedInMapleOS
              onInvalidChat={() => onClose(activeTab.id)}
            />
          </div>
        ) : (
          <div className="h-full overflow-hidden px-4 pb-4">
            <div className="h-full overflow-hidden rounded-2xl border border-border/70 bg-background shadow-sm">
              <MapleWord
                key={activeTab.id}
                filePath={activeTab.filePath}
                initialContent={activeTab.initialContent}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIProjectWorkspace;
